import { activeEvents } from './projection';
import type { ClinicalEvent } from './types';

export const numeric = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
export function scopedLaborEvents(events: ClinicalEvent[], episodeId: string, encounterId: string, now: number) {
  // Resolve amendments before scope/status filtering so an old head can never reappear.
  return activeEvents(events)
    .filter(
      (e) =>
        e.episode_id === episodeId &&
        e.encounter_id === encounterId &&
        Number.isFinite(Date.parse(e.occurred_at)) &&
        Date.parse(e.occurred_at) <= now,
    )
    .sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at) || a.sequence - b.sequence);
}
export function laborAnchor(events: ClinicalEvent[], motherId: string, mode: 'ADMITTED' | 'ACTIVE_LABOR') {
  return events.find((e) => e.subject_id === motherId && e.event_type === 'MILESTONE' && e.payload.milestone === mode);
}
export function laborMetrics(events: ClinicalEvent[], motherId: string, fetusId: string, now: number) {
  const mother = events.filter((e) => e.subject_id === motherId);
  const birth = events.find((e) => e.subject_id === fetusId && e.event_type === 'BIRTH');
  const end = birth ? Date.parse(birth.occurred_at) : now;
  const elapsed = (e?: ClinicalEvent, until = now) =>
    e && until >= Date.parse(e.occurred_at) ? (until - Date.parse(e.occurred_at)) / 60000 : null;
  const milestone = (name: string) => mother.find((e) => e.event_type === 'MILESTONE' && e.payload.milestone === name);
  const quantity = (type: string) => {
    const rows = mother.filter((e) => e.event_type === type);
    const amounts = rows.map((e) => e.payload.amount_ml).filter(numeric);
    return {
      value: amounts.length ? amounts.reduce((a, b) => a + b, 0) : null,
      unquantified: rows.length - amounts.length,
    };
  };
  const progress = mother.filter((e) => e.event_type === 'LABOR.PROGRESS' && numeric(e.payload.dilation_cm));
  let change: ClinicalEvent | undefined;
  for (let i = 1; i < progress.length; i++)
    if (progress[i].payload.dilation_cm !== progress[i - 1].payload.dilation_cm) change = progress[i];
  return {
    totalLabor: elapsed(milestone('LABOR_START'), end),
    activeLabor: elapsed(milestone('ACTIVE_LABOR'), end),
    sinceRom: elapsed(
      mother.find((e) => e.event_type === 'ROM'),
      end,
    ),
    pushing: elapsed(milestone('PUSHING_START'), end),
    lastFhr: elapsed(events.filter((e) => e.subject_id === fetusId && e.event_type === 'LABOR.FHR').at(-1)),
    lastVitals: elapsed(
      mother
        .filter((e) => ['VITALS.BP', 'VITALS.PULSE', 'VITALS.TEMP', 'VITALS.RR', 'VITALS.SPO2'].includes(e.event_type))
        .at(-1),
    ),
    cervicalChange: elapsed(change, end),
    intake: quantity('IO.INTAKE'),
    output: quantity('IO.OUTPUT'),
    born: !!birth,
  };
}
export interface TapSample {
  offset_ms: number;
  bpm: number;
  contraction: boolean;
}
export function tracerWindows(event: ClinicalEvent) {
  const source = event.payload.tracer_samples;
  if (!Array.isArray(source)) return [];
  let previous = 0;
  return source.flatMap((s: TapSample) => {
    if (!s || !numeric(s.offset_ms) || s.offset_ms <= previous || !numeric(s.bpm) || s.bpm < 0 || s.bpm > 300)
      return [];
    const start = previous;
    previous = s.offset_ms;
    return [{ ...s, start, end: s.offset_ms }];
  });
}
export function durationLabel(minutes: number | null) {
  if (minutes === null) return 'Not documented';
  const whole = Math.floor(minutes);
  return whole >= 60 ? `${Math.floor(whole / 60)}h ${whole % 60}m` : `${whole}m`;
}
